import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Loader2, ShieldX } from "lucide-react";
import { Logo } from "@/components/ui/logo";

export function AdminGuard({ children }: { children: React.ReactNode }) {
  const { user, loading, isAuthStatusKnown, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  useEffect(() => {
    // Only redirect once we know authentication status
    if (isAuthStatusKnown) {
      if (!loading && !user) {
        // Save the location they were trying to access for redirect after login
        navigate(`/login?from=${encodeURIComponent(location.pathname)}`);
      } else if (!loading && user && !isAdmin) {
        // User is authenticated but not admin, show access denied message
        setIsCheckingAuth(false);
      } else if (!loading && user && isAdmin) {
        // User is authenticated and is admin, allow access
        setIsCheckingAuth(false);
      }
    }
  }, [user, isAdmin, loading, navigate, location.pathname, isAuthStatusKnown]);

  // Show loading while checking auth or fetching user data
  if (loading || isCheckingAuth) {
    return (
      <div className="h-screen w-screen flex flex-col items-center justify-center bg-background">
        <div className="animate-pulse mb-8">
          <Logo variant="full" size="lg" />
        </div>
        <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
        <p className="text-muted-foreground">Verificando permissões de administrador...</p>
      </div>
    );
  }

  // Show access denied if user is not admin
  if (user && !isAdmin && !isCheckingAuth) {
    return (
      <div className="h-screen w-screen flex flex-col items-center justify-center bg-background">
        <ShieldX className="h-16 w-16 text-destructive mb-4" />
        <h1 className="text-2xl font-bold mb-2">Acesso Restrito</h1>
        <p className="text-muted-foreground mb-6">Você não tem permissão para acessar esta área administrativa.</p>
        <p className="text-sm text-muted-foreground mb-6">Esta área é restrita a administradores do sistema. Se você precisa de acesso administrativo, entre em contato com o administrador principal.</p>
        <div className="flex gap-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
          >
            Voltar para o Dashboard
          </button>
          <button
            onClick={() => navigate('/settings')}
            className="px-4 py-2 border border-border rounded-md hover:bg-accent transition-colors"
          >
            Configurações da Conta
          </button>
        </div>
      </div>
    );
  }

  return user && isAdmin ? <>{children}</> : null;
}

export default AdminGuard;
